
import { useAtom } from "jotai";
import { FC, useEffect, useMemo } from "react";
import NoPlansAvailable from "./NoPlansAvailable";
import OfferedPlans from "./OfferedPlans";
import PlanDescription from "./PlanDescription";
import style from "./style.module.css";
import TableFooter from "./TableFooter";
import TableHeader from "./TableHeader";
import { planTableSelectedPlanAtom } from "@utils/store/store";
import { BatchPlan, BatchPlus } from "../batch.type";
import { getPlusDefaultPlan } from "@utils/utilities";
import BatchPlusLoader from "../BatchPlusLoader";

interface props {
  onClose: (clickFrom?: string, plan?: any) => void;
  config: BatchPlus;
  batchName: string;
  pageName?: string;
  ctaName?: string;
  selectedPlanId?: string;
  openInIframe?: boolean;
  slug?: string;
  isLoading?: boolean;
  selectHighestPlan?: boolean;
}

const ComparisonTable: FC<props> = ({
  onClose,
  config,
  batchName,
  pageName,
  selectedPlanId,
  openInIframe,
  ctaName,
  slug,
  isLoading,
  selectHighestPlan,
}) => {
  /* Atom to maintain state with in table only. */
  const [selectedPlan, setSelectedPlan] = useAtom(planTableSelectedPlanAtom);

  /* fetch selected plans index from available response. */
  const selectedIndex = config?.plans?.findIndex(
    (item: any) => item?._id === selectedPlan?._id,
  );

  /* If any previous plan is purchased -> next plan should have upgrade now cta. */

  const isAnyPlanPurchased = useMemo(
    () => config?.plans?.find((plan: any) => plan?.isPlanPurchased),
    [config],
  );

  useEffect(() => {
    /* get next plan after last purchased plan else show 1st plan as selected plan. */
    if (
      config?.plans?.length > 0 &&
      !selectedPlan?._id &&
      !selectedPlanId &&
      !selectHighestPlan
    ) {
      // let lastPurchasedPlanIdx = findLastIndex(
      //   config?.plans,
      //   (plan: BatchPlan) => plan?.isPlanPurchased,
      // );

      // let activeIndex =
      //   lastPurchasedPlanIdx < config.plans.length - 1
      //     ? lastPurchasedPlanIdx + 1
      //     : lastPurchasedPlanIdx;
      const recommendedPlanIndex = config?.plans?.findIndex(
        (plan: BatchPlan) => plan?.isRecommended,
      );

      let activeIndex =
        recommendedPlanIndex >= 0
          ? recommendedPlanIndex
          : config?.plans?.length - 1;

      setSelectedPlan(config.plans[activeIndex]);
    }
  }, [config, selectedPlan, selectedPlanId, selectHighestPlan]);

  const isAnyPlanAvailable = () => {
    return config?.comparisonTable?.length > 0;
  };

  const handlePopUpActionEvent = (actionType: string, plan?: BatchPlan) => {
    const planSelected = plan || selectedPlan;
    const defaultPlan = getPlusDefaultPlan(config);

    const currentPlan = config?.plans?.find(
      (plan: any) => plan?.isPlanPurchased,
    );

    const payload = {
      action_type: actionType,
      view_type: "table",
      page_name: pageName,
      current_plan: currentPlan?.title || "",
      default_plan: defaultPlan?.title || "",
      plan_selected: planSelected?.title || "",
      popup_cta_name: ctaName,
      inside_popup: "yes",
      batch_name: batchName,
      batch_id: slug,
    };
  };

  return (
    <section className={style.wrapper}>
      {/* render table header with close modal logic */}
      <TableHeader
        batchName={batchName}
        onClose={onClose}
        triggerEvent={handlePopUpActionEvent}
      />
      {isLoading ? (
        <BatchPlusLoader />
      ) : isAnyPlanAvailable() ? (
        <>
          {/* render plans card only */}
          <OfferedPlans
            plans={config?.plans}
            selectedPlanId={selectedPlanId || ""}
            selectHighestPlan={selectHighestPlan}
            triggerEvent={handlePopUpActionEvent}
          />
          {/* render core logic for table i.e offering and selection. */}
          <PlanDescription
            selectedIndex={selectedIndex}
            config={config} // tableConfig is needed to render plans as well as bundle name.
            triggerEvent={handlePopUpActionEvent}
            slug={slug || ""}
            pageName={pageName || ""}
            ctaName={ctaName || ""}
            onClose={onClose}
            openInIframe={openInIframe}
          />
          {/* render buy now cta + price and discount logic */}
          {!selectedPlan?.isPlanPurchased && (
            <TableFooter
              slug={slug || ""}
              pageName={pageName || ""}
              ctaName={ctaName || ""}
              onClose={onClose}
              isAnyPlanPurchased={!!isAnyPlanPurchased?._id}
              openInIframe={openInIframe}
              triggerEvent={handlePopUpActionEvent}
            />
          )}
        </>
      ) : (
        <NoPlansAvailable />
      )}
    </section>
  );
};

export default ComparisonTable;
